import { useState, useEffect } from 'react'
import {
  Box,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
} from '@mui/material'
import { Add, Edit, Delete, Category } from '@mui/icons-material'
import { PageHeader, Button, DataTable, Card, Modal, PermissionGate } from '@components/common'
import { usePermissions } from '@context/PermissionContext'
import { toast } from 'react-toastify'
import api from '@services/api'

const columns = [
  { field: 'code', headerName: 'Code', sortable: true, width: 120 },
  { field: 'name', headerName: 'Name', sortable: true },
  { field: 'description', headerName: 'Description', sortable: false },
  {
    field: 'parent_name',
    headerName: 'Parent Category',
    sortable: true,
    renderCell: (value) => value || '-',
  },
  {
    field: 'is_active',
    headerName: 'Status',
    width: 100,
    renderCell: (value) => (
      <Chip
        label={value ? 'Active' : 'Inactive'}
        color={value ? 'success' : 'default'}
        size="small"
      />
    ),
  },
  {
    field: 'created_at',
    headerName: 'Created',
    sortable: true,
    renderCell: (value) => value ? new Date(value).toLocaleDateString() : '-',
  },
]

const initialFormData = {
  name: '',
  code: '',
  description: '',
  parent: '',
  is_active: true,
}

const InventoryCategories = () => {
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [formOpen, setFormOpen] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState(null)
  const [formData, setFormData] = useState(initialFormData)
  const [formLoading, setFormLoading] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [errors, setErrors] = useState({})
  const { canEdit, canDelete } = usePermissions()

  const fetchCategories = async () => {
    try {
      setLoading(true)
      const response = await api.get('/inventory/categories/')
      const data = response.data.results || response.data
      // Add parent_name to each category
      const categoriesWithParent = data.map(cat => ({
        ...cat,
        parent_name: cat.parent ? data.find(c => c.id === cat.parent)?.name : null
      }))
      setCategories(categoriesWithParent)
    } catch (error) {
      console.error('Error fetching categories:', error)
      toast.error('Failed to load categories')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchCategories()
  }, [])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }))
    }
  }

  const handleSwitchChange = (e) => {
    const { name, checked } = e.target
    setFormData((prev) => ({ ...prev, [name]: checked }))
  }

  const handleEdit = (category) => {
    setSelectedCategory(category)
    setFormData({
      name: category.name || '',
      code: category.code || '',
      description: category.description || '',
      parent: category.parent || '',
      is_active: category.is_active !== false,
    })
    setFormOpen(true)
  }

  const handleSubmit = async () => {
    if (!formData.name || !formData.code) {
      setErrors({
        name: !formData.name ? 'Name is required' : '',
        code: !formData.code ? 'Code is required' : '',
      })
      return
    }

    setFormLoading(true)
    try {
      const payload = {
        ...formData,
        parent: formData.parent || null,
      }

      if (selectedCategory) {
        await api.patch(`/inventory/categories/${selectedCategory.id}/`, payload)
        toast.success('Category updated successfully')
      } else {
        await api.post('/inventory/categories/', payload)
        toast.success('Category created successfully')
      }

      fetchCategories()
      handleCloseForm()
    } catch (error) {
      console.error('Error saving category:', error)
      if (error.response?.data) {
        const backendErrors = {}
        Object.entries(error.response.data).forEach(([key, value]) => {
          backendErrors[key] = Array.isArray(value) ? value[0] : value
        })
        setErrors(backendErrors)
      }
      toast.error('Failed to save category')
    } finally {
      setFormLoading(false)
    }
  }

  const handleDelete = async () => {
    if (!selectedCategory) return
    setDeleting(true)
    try {
      await api.delete(`/inventory/categories/${selectedCategory.id}/`)
      toast.success('Category deleted successfully')
      fetchCategories()
      setDeleteModalOpen(false)
      setSelectedCategory(null)
    } catch (error) {
      console.error('Error deleting category:', error)
      toast.error('Failed to delete category. It may be in use.')
    } finally {
      setDeleting(false)
    }
  }

  const handleCloseForm = () => {
    setFormOpen(false)
    setSelectedCategory(null)
    setFormData(initialFormData)
    setErrors({})
  }

  const actionsColumn = {
    field: 'actions',
    headerName: '',
    width: 100,
    renderCell: (_, row) => (
      <Box display="flex" gap={1}>
        {canEdit('inventory') && (
          <Button
            size="small"
            variant="text"
            onClick={(e) => {
              e.stopPropagation()
              handleEdit(row)
            }}
          >
            <Edit fontSize="small" />
          </Button>
        )}
        {canDelete('inventory') && (
          <Button
            size="small"
            variant="text"
            color="error"
            onClick={(e) => {
              e.stopPropagation()
              setSelectedCategory(row)
              setDeleteModalOpen(true)
            }}
          >
            <Delete fontSize="small" />
          </Button>
        )}
      </Box>
    ),
  }

  return (
    <Box>
      <PageHeader
        title="Inventory Categories"
        subtitle="Manage categories for spare parts, reagents, and supplies"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Inventory', path: '/inventory' },
          { label: 'Categories' },
        ]}
        actions={
          <PermissionGate permission="inventory.create">
            <Button startIcon={<Add />} onClick={() => setFormOpen(true)}>
              Add Category
            </Button>
          </PermissionGate>
        }
      />

      <Card>
        <DataTable
          columns={[...columns, actionsColumn]}
          data={categories}
          loading={loading}
          searchPlaceholder="Search categories..."
          emptyMessage="No categories found. Create your first category to organize inventory items."
        />
      </Card>

      {/* Category Form Dialog */}
      <Dialog open={formOpen} onClose={handleCloseForm} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <Category color="primary" />
            {selectedCategory ? 'Edit Category' : 'Add Category'}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Category Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                error={Boolean(errors.name)}
                helperText={errors.name}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Code"
                name="code"
                value={formData.code}
                onChange={handleChange}
                error={Boolean(errors.code)}
                helperText={errors.code || 'Unique identifier (e.g., FILTERS, REAGENTS)'}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Parent Category (Optional)</InputLabel>
                <Select
                  name="parent"
                  value={formData.parent}
                  onChange={handleChange}
                  label="Parent Category (Optional)"
                >
                  <MenuItem value="">
                    <em>None (Top Level)</em>
                  </MenuItem>
                  {categories
                    .filter(c => c.id !== selectedCategory?.id)
                    .map((cat) => (
                      <MenuItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={handleSwitchChange}
                    name="is_active"
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={handleCloseForm}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={formLoading}>
            {selectedCategory ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Modal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Delete Category"
        maxWidth="xs"
        actions={
          <>
            <Button variant="outlined" onClick={() => setDeleteModalOpen(false)} disabled={deleting}>
              Cancel
            </Button>
            <Button color="error" onClick={handleDelete} loading={deleting}>
              Delete
            </Button>
          </>
        }
      >
        Are you sure you want to delete "{selectedCategory?.name}"? Items using this category will have their category unassigned.
      </Modal>
    </Box>
  )
}

export default InventoryCategories
